import React, { useEffect, useState } from 'react';
import { Button, Dropdown, Menu } from 'antd';
import { DownOutlined, EllipsisOutlined } from '@ant-design/icons';
import classnames from 'classnames';
import dayjs from 'dayjs';
import { AttendState, MeetingRecord } from 'type/meeting';
import { MeetingOptionType } from 'type/meetingDetail';
import { formatEndTime } from 'util/format';
import { getSchedulePresentation, ScheduleAction, SCHEDULE_ACTION_LABELS } from './schedulePresentation';
import style from './ScheduleMeetingCard.module.scss';

type ActionEvent = { stopPropagation: () => void };

interface IProps {
  record: MeetingRecord;
  isCreator: boolean;
  isExtending: boolean;
  roomInfo: string;
  onCheckout: () => void;
  onAction: (action: ScheduleAction, event: ActionEvent) => void;
  onCancel: (event: ActionEvent, type: MeetingOptionType) => void;
}

const ScheduleMeetingCard: React.FC<IProps> = ({ record, isCreator, isExtending, roomInfo, onCheckout, onAction, onCancel }) => {
  const [now, setNow] = useState(Date.now);
  const [openMenu, setOpenMenu] = useState<'invitation' | 'more' | null>(null);
  const { meeting } = record;
  const presentation = getSchedulePresentation(record, isCreator, now);
  const { tags, invitation, primaryAction, moreActions, conflict, signed } = presentation;
  const beginTime = dayjs(meeting.beginTime).format('HH:mm');
  const endTime = formatEndTime(dayjs(meeting.endTime).format('HH:mm'));

  useEffect(() => {
    // 在开始前十分钟、结束时重新计算，避免停留页面时保留过期操作。
    const nextBoundary = [meeting.beginTime - 10 * 60 * 1000, meeting.beginTime, meeting.endTime].find((time) => time > Date.now());
    if (nextBoundary === undefined) return;
    const timer = window.setTimeout(() => setNow(Date.now()), Math.min(nextBoundary - Date.now() + 1, 2147483647));
    return () => window.clearTimeout(timer);
  }, [meeting.beginTime, meeting.endTime, now]);

  useEffect(() => {
    setNow(Date.now());
    setOpenMenu(null);
  }, [record.inviteState, meeting.meetingNo, meeting.status, meeting.beginTime, meeting.endTime]);

  const handleAction = (action: ScheduleAction, event: ActionEvent) => {
    event.stopPropagation();
    setOpenMenu(null);
    onAction(action, event);
  };

  const handleCancel = (type: MeetingOptionType, event: ActionEvent) => {
    event.stopPropagation();
    setOpenMenu(null);
    onCancel(event, type);
  };

  const getMenuActionLabel = (action: ScheduleAction) => {
    const isAttendanceAction = action === 'join' || action === 'leave' || action === 'refuse';
    return isAttendanceAction && record.inviteState !== AttendState.TO_BE_CONFIRMED
      ? `改为${SCHEDULE_ACTION_LABELS[action]}`
      : SCHEDULE_ACTION_LABELS[action];
  };

  const renderMenu = (actions: ScheduleAction[]) => (
    <Menu selectable={false} className={style.actionMenu} onClick={({ domEvent }) => domEvent.stopPropagation()}>
      {actions.map((action) => action === 'cancel' && meeting.isRepeatMeeting ? (
        <Menu.SubMenu key={action} title={SCHEDULE_ACTION_LABELS[action]} popupClassName={style.repeatMenu}>
          <Menu.Item key="cancelThis" onClick={({ domEvent }) => handleCancel(MeetingOptionType.ThisMeeting, domEvent)}>取消本次会议</Menu.Item>
          <Menu.Item key="cancelPart" onClick={({ domEvent }) => handleCancel(MeetingOptionType.PartMeeting, domEvent)}>取消本次及后续周期会议</Menu.Item>
          <Menu.Item key="cancelAll" onClick={({ domEvent }) => handleCancel(MeetingOptionType.AllMeeting, domEvent)}>取消所有周期会议</Menu.Item>
        </Menu.SubMenu>
      ) : (
        <Menu.Item key={action} disabled={action === 'extend' && isExtending} onClick={({ domEvent }) => handleAction(action, domEvent)}>
          {getMenuActionLabel(action)}
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <div className={style.meeting}>
      <div className={style.timeline}>
        <span>{beginTime}</span>
        <span className={style.dot} />
      </div>
      <div className={style.card} onClick={onCheckout}>
        <div className={style.content}>
          <div className={style.heading}>
            <span className={style.subject} title={meeting.subject}>{meeting.subject}</span>
            {tags.map((tag) => <span key={tag.text} className={classnames(style.tag, style[tag.tone])}>{tag.text}</span>)}
          </div>
          <div className={classnames(style.meta, conflict && style.conflict)}>{beginTime} - {endTime}</div>
          <div className={style.meta} title={roomInfo}>
            {roomInfo}
            {signed && <span className={style.signed}>会议室已签到</span>}
          </div>
        </div>
        <div className={style.actions} onClick={(event) => event.stopPropagation()}>
          {invitation && (
            <Dropdown overlay={renderMenu(invitation.actions)} trigger={['click']} placement="bottomRight" visible={openMenu === 'invitation'} onVisibleChange={(visible) => setOpenMenu(visible ? 'invitation' : null)}>
              <Button size="small" className={classnames(style.invitation, style[invitation.tone], record.inviteState === AttendState.TO_BE_CONFIRMED && style.newInvitation)} aria-haspopup="menu" aria-expanded={openMenu === 'invitation'}>
                {invitation.text}<DownOutlined />
              </Button>
            </Dropdown>
          )}
          {primaryAction && (
            <Button size="small" type="primary" className={style.primaryAction} loading={primaryAction === 'extend' && isExtending} onClick={(event) => handleAction(primaryAction, event)}>
              {SCHEDULE_ACTION_LABELS[primaryAction]}
            </Button>
          )}
          <Dropdown overlay={renderMenu(moreActions)} trigger={['click']} placement="bottomRight" visible={openMenu === 'more'} onVisibleChange={(visible) => setOpenMenu(visible ? 'more' : null)}>
            <Button size="small" className={style.more} icon={<EllipsisOutlined />} aria-label="更多操作" aria-haspopup="menu" aria-expanded={openMenu === 'more'} />
          </Dropdown>
        </div>
      </div>
    </div>
  );
};

export default ScheduleMeetingCard;
